import type { PropsWithChildren, Ref } from 'react'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { cx } from '../../cx'

const skeletonVariants = tv({
  base: 'bg-sand',
  variants: {
    animate: {
      none: '',
      pulse: 'animate-pulse',
    },
    variant: {
      rectangle: 'rounded-none',
      rounded: 'rounded-xl',
      circle: 'rounded-full',
    },
    size: {
      full: 'size-full',
    },
  },
  defaultVariants: {
    variant: 'rounded',
    size: 'full',
    animate: 'pulse',
  },
})

export type SkeletonProps = VariantProps<typeof skeletonVariants> & {
  className?: string
  ref?: Ref<HTMLDivElement>
} & React.HTMLAttributes<HTMLDivElement>

export function Skeleton(props: SkeletonProps) {
  const { ref, variant, size, animate, className, ...rest } = props

  return (
    <div
      ref={ref}
      className={skeletonVariants({
        variant,
        size,
        animate,
        className,
      })}
      {...rest}
    />
  )
}

export const SkeletonWrapper = ({
  children,
  isLoading,
  classNames,
  width,
  hidden,
}: PropsWithChildren<{
  isLoading?: boolean
  classNames?: { root?: string; skeleton?: string }
  width?: '1/6' | '2/6' | '3/6' | '4/6' | '5/6'
  hidden?: boolean
}>) => {
  if (hidden) return null

  return (
    <div className={cx('relative', { '*:first:invisible': isLoading }, classNames?.root)}>
      {children}
      {isLoading && (
        <Skeleton
          className={cx(
            'absolute inset-0',
            {
              'w-1/6': width === '1/6',
              'w-2/6': width === '2/6',
              'w-3/6': width === '3/6',
              'w-4/6': width === '4/6',
              'w-5/6': width === '5/6',
            },
            classNames?.skeleton
          )}
        />
      )}
    </div>
  )
}
